import React, { useEffect, useState } from 'react';
import { useAuth } from '../../commons/Auth';
import { useTranslation } from 'react-i18next';
import { useLoading } from '../../commons/Loading';
import { useAlert } from 'react-alert';
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import contractsAPIs, {
  deleteRecurringFeeFile,
} from '../../lib/fetch/contractsAPIs';
import RecurringFeesPresentational from './RecurringFeesPresentational';
import { getSignedUrl } from '../../lib/fetch/files';
import axios from 'axios';

const { REACT_APP_DEFAULT_PAGINATION_LIMIT = 50 } = process.env;

const MyRecurringFees = ({ profile }) => {
  const [{ newToken } = {}] = useAuth();
  const [t] = useTranslation();
  const { showLoading, hideLoading } = useLoading();
  const alert = useAlert();

  const [hasImportedContracts, setHasImportedContracts] = useState(false);
  const [hasImportedContractsPolis, setHasImportedContractsPolis] = useState(
    false
  );
  const [
    contractsImportFeedbackData,
    setContractsImportFeedbackData,
  ] = useState({});
  const [
    contractsImportFeedbackDataPolis,
    setContractsImportFeedbackDataPolis,
  ] = useState({});

  const [recurringFees, setRecurringFees] = useState([]);
  const [total, setTotal] = useState(0);
  const [formattedCSVContracts, setFormattedCSVContracts] = useState([]);

  const [totPod, setTotPod] = useState(0);
  const [totPdr, setTotPdr] = useState(0);

  const [query, setQuery] = useQueryParams({
    limit: withDefault(NumberParam, +REACT_APP_DEFAULT_PAGINATION_LIMIT),
    offset: NumberParam,
    pod_pdr: withDefault(StringParam, ''),
    admin: withDefault(StringParam, ''),
    supplier: withDefault(StringParam, ''),
    customer: withDefault(StringParam, ''),
    dateFeeYear: withDefault(NumberParam, new Date().getFullYear()),
    dateFeeMonth: NumberParam,
    paid: withDefault(StringParam, ''),
    contractType: withDefault(StringParam, ''),
  });

  const {
    limit,
    offset,
    pod_pdr,
    admin,
    supplier,
    customer,
    dateFeeYear,
    dateFeeMonth,
    paid,
    contractType,
  } = query;

  const toggleShowImportFeedback = () => setHasImportedContracts((v) => !v);
  const toggleShowImportFeedbackPolis = () =>
    setHasImportedContractsPolis((v) => !v);

  function onFilter(queryDiff) {
    if (
      (queryDiff.admin && queryDiff.admin !== admin) || // first search
      (admin && queryDiff.admin === '') || // search deleted
      (queryDiff.pod_pdr && queryDiff.pod_pdr !== pod_pdr) || // first search
      (pod_pdr && queryDiff.pod_pdr === '') ||
      (queryDiff.dateFeeYear && queryDiff.dateFeeYear !== dateFeeYear) || // first search
      (dateFeeYear && queryDiff.dateFeeYear === '') || // search deleted
      (queryDiff.dateFeeMonth && queryDiff.dateFeeMonth !== dateFeeMonth) || // first search
      (dateFeeMonth && queryDiff.dateFeeMonth === '') || // search deleted
      (queryDiff.customer && queryDiff.customer !== customer) || // first search
      (customer && queryDiff.customer === '') || // search deleted
      (queryDiff.supplier && queryDiff.supplier !== supplier) || // first search
      (supplier && queryDiff.supplier === '') || // search deleted
      (queryDiff.paid && queryDiff.paid !== paid) || // first search
      (paid && queryDiff.paid === '') || // search deleted
      (queryDiff.contractType && queryDiff.contractType !== contractType) || // first search
      (contractType && queryDiff.contractType === '') || // search deleted
      (queryDiff.limit && queryDiff.limit !== limit)
    ) {
      queryDiff.offset = 0; // return to first page
    }

    setQuery(queryDiff);
  }

  const fetchRecurringFees = async (query) => {
    showLoading();

    const {
      data,
      formattedCSVContracts,
      total: _total,
      totPdr,
      totPod,
    } = await contractsAPIs.findUserRecurrentContracts({
      newToken,
      query,
      userId:
        profile?.type === 'ADMIN_COLLAB' ? profile?.adminId : profile?._id,
    });
    setTotal(Number(_total || 0));
    setTotPod(totPod);
    setTotPdr(totPdr);
    setRecurringFees(data);

    hideLoading();
  };

  const fetchRecurringFeesForExport = async (query) => {
    const {
      data,
      total: _total,
      formattedCSVContracts = [],
    } = await contractsAPIs.findForExportRecurring({
      newToken,
      query,
      userId:
        profile?.type === 'ADMIN_COLLAB' ? profile?.adminId : profile?._id,
    });
    setFormattedCSVContracts(formattedCSVContracts);
  };

  useEffect(() => {
    if (profile?._id) {
      fetchRecurringFees({
        limit,
        offset,
        pod_pdr,
        admin,
        customer,
        supplier,
        dateFeeYear,
        dateFeeMonth,
        paid,
        contractType,
      });

      if (profile?.polis === true) {
        fetchRecurringFeesForExport({
          limit: 99999,
          offset: 0,
          pod_pdr,
          admin,
          customer,
          supplier,
          dateFeeYear,
          dateFeeMonth,
          paid,
          contractType,
        });
      }
    }
  }, [
    profile,
    limit,
    offset,
    pod_pdr,
    admin,
    customer,
    supplier,
    dateFeeYear,
    dateFeeMonth,
    paid,
    contractType,
  ]);

  const onAddPayment = async ({ contractId, data }) => {
    showLoading();

    const endpoint =
      profile.polis === true
        ? `${process.env.REACT_APP_API_URL}/contracts/recurrent/${contractId}/payments-polis`
        : `${process.env.REACT_APP_API_URL}/contracts/recurrent/${contractId}/payments`;

    let response;
    try {
      response = await axios.put(endpoint, data, {
        headers: { Authorization: newToken },
      });
    } catch (error) {
      response = error;
    }

    if (response instanceof Error) {
      hideLoading();
      const detail =
        response?.response?.data?.message || t('COMMON:GENERIC_ERROR');
      return alert.error(detail);
    }

    hideLoading();

    alert.success(t('COMMON:SUCCESS_UPDATE'));
    fetchRecurringFees({
      limit,
      offset,
      pod_pdr,
      admin,
      customer,
      supplier,
      dateFeeYear,
      dateFeeMonth,
      paid,
      contractType,
    });
  };

  const onImportContractPaids = async (file) => {
    showLoading();
    const { error, data } = await contractsAPIs.importContractsPaids({
      profileId: profile._id,
      newToken,
      file,
    });
    hideLoading();
    toggleShowImportFeedback();
    setContractsImportFeedbackData({
      ...data,
      validationErrors: error?.response?.data,
    });
    fetchRecurringFees({
      limit,
      offset,
      pod_pdr,
      admin,
      customer,
      supplier,
      dateFeeYear,
      dateFeeMonth,
      paid,
      contractType,
    });
    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
    }
  };

  const onImportContractPaidsPolis = async (file) => {
    showLoading();
    const { error, data } = await contractsAPIs.importContractsPaidsPolis({
      profileId: profile._id,
      newToken,
      file,
    });
    hideLoading();
    toggleShowImportFeedbackPolis();
    setContractsImportFeedbackDataPolis({
      ...data,
      validationErrors: error?.response?.data,
    });
    fetchRecurringFees({
      limit,
      offset,
      pod_pdr,
      admin,
      customer,
      supplier,
      dateFeeYear,
      dateFeeMonth,
      paid,
      contractType,
    });
    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
    }
  };

  async function handleFileListView(key) {
    showLoading();

    const { data, error } = await getSignedUrl(key, newToken);

    if (error) {
      hideLoading();
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    hideLoading();
    window.open(data.url, '_blank');
  }

  async function handleDelete({ contractId, key }) {
    showLoading();

    const { error, data } = await deleteRecurringFeeFile({
      contractId,
      key,
      newToken,
    });

    if (error) {
      hideLoading();

      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    fetchRecurringFees({
      limit,
      offset,
      pod_pdr,
      admin,
      customer,
      supplier,
      dateFeeYear,
      dateFeeMonth,
      paid,
      contractType,
    });
    alert.success(t('COMMON:SUCCESS_DELETE'));
  }

  return (
    <RecurringFeesPresentational
      profile={profile}
      data={recurringFees}
      formattedData={formattedCSVContracts}
      query={query}
      total={total}
      totPod={totPod}
      totPdr={totPdr}
      onFilter={onFilter}
      onStatusChanged={() => fetchRecurringFees()}
      handleFileListView={handleFileListView}
      handleDelete={handleDelete}
      onImportContractPaids={onImportContractPaids}
      onImportContractPaidsPolis={onImportContractPaidsPolis}
      contractsImportFeedbackData={contractsImportFeedbackData}
      contractsImportFeedbackDataPolis={contractsImportFeedbackDataPolis}
      hasImportedContracts={hasImportedContracts}
      hasImportedContractsPolis={hasImportedContractsPolis}
      toggleShowImportFeedback={toggleShowImportFeedback}
      toggleShowImportFeedbackPolis={toggleShowImportFeedbackPolis}
      onAddPayment={onAddPayment}
    />
  );
};

export default MyRecurringFees;
